import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from '../utils/api';

const initialForm = {
  name: '',
  region_type: 'State',
  sort_order: 0,
  is_active: true,
};

const IndiaStateManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [confirmDialog, setConfirmDialog] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    onConfirm: null,
  });

  const fetchItems = async (message = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/india-states?include_inactive=true');
      setItems(response.data || []);
      if (message) {
        setSuccess(message);
      }
    } catch (err) {
      setError('Failed to load States / UTs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const resetForm = () => {
    setFormData(initialForm);
    setEditingItem(null);
    setShowModal(false);
  };

  const openCreate = () => {
    setFormData(initialForm);
    setEditingItem(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setFormData({
      name: item.name,
      region_type: item.region_type,
      sort_order: item.sort_order,
      is_active: item.is_active,
    });
    setEditingItem(item);
    setShowModal(true);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    try {
      setSaving(true);
      const payload = {
        ...formData,
        sort_order: Number(formData.sort_order) || 0,
      };
      if (editingItem) {
        await axios.put(`/api/india-states/${editingItem.id}`, payload);
        await fetchItems('State / UT updated successfully');
      } else {
        await axios.post('/api/india-states', payload);
        await fetchItems('State / UT created successfully');
      }
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save state / UT');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      show: true,
      title: 'Delete State / UT',
      message: 'Delete this state / UT?',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await axios.delete(`/api/india-states/${id}`);
          await fetchItems('State / UT deleted successfully');
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to delete state / UT');
        }
      },
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ show: false, title: '', message: '', confirmText: 'Confirm', onConfirm: null });
  };

  const executeConfirmDialogAction = async () => {
    const action = confirmDialog.onConfirm;
    closeConfirmDialog();
    if (typeof action === 'function') {
      await action();
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>States and UT Configuration</h1>
          <p className="text-muted mb-0">Manage the India state and union territory options available for buyer addresses.</p>
        </Col>
        <Col xs="auto">
          <Button onClick={openCreate}>Add State / UT</Button>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading state list...</span>
              </Spinner>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Sort Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{item.region_type}</td>
                    <td>{item.sort_order}</td>
                    <td>
                      <Badge bg={item.is_active ? 'success' : 'secondary'}>
                        {item.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-secondary" onClick={() => openEdit(item)}>Edit</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item.id)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={resetForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingItem ? 'Edit State / UT' : 'Add State / UT'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control name="name" value={formData.name} onChange={handleChange} maxLength={40} required />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select name="region_type" value={formData.region_type} onChange={handleChange}>
                    <option value="State">State</option>
                    <option value="UT">UT</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sort Order</Form.Label>
                  <Form.Control type="number" name="sort_order" value={formData.sort_order} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>
            <Form.Check type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} label="Active" />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={resetForm}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={confirmDialog.show} onHide={closeConfirmDialog} centered>
        <Modal.Header closeButton>
          <Modal.Title>{confirmDialog.title || 'Confirm Action'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{confirmDialog.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeConfirmDialog}>Cancel</Button>
          <Button variant="danger" onClick={executeConfirmDialogAction}>
            {confirmDialog.confirmText || 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default IndiaStateManager;
